import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  BookOpen, Calendar, Clock, ArrowRight, 
  TrendingUp, Lightbulb, Code, Zap
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import MarketingHeader from '@/components/MarketingHeader';
import MarketingFooter from '@/components/MarketingFooter';
import SEO from '@/components/SEO';

const featuredPosts = [
  {
    title: 'Why Your Website Needs to Work for You 24/7',
    excerpt: 'Your website is your most hardworking employee. Learn how to make sure it\'s earning its keep around the clock.',
    category: 'Digital Strategy',
    readTime: '5 min read',
    date: 'Coming Soon',
    icon: TrendingUp,
    gradient: 'from-blue-500 to-indigo-500'
  },
  {
    title: 'The FADDs of Business: Overcoming Fear, Anxiety, Doubt, and Depression',
    excerpt: 'How entrepreneurs can apply the FADDs framework to navigate the emotional challenges of running a business.',
    category: 'Entrepreneurship',
    readTime: '7 min read',
    date: 'Coming Soon',
    icon: Lightbulb,
    gradient: 'from-violet-500 to-purple-500'
  },
  {
    title: 'AI Automation: What Small Businesses Actually Need',
    excerpt: 'Cutting through the hype to show you which AI tools deliver real value for small business operations.',
    category: 'Technology',
    readTime: '6 min read',
    date: 'Coming Soon',
    icon: Code,
    gradient: 'from-emerald-500 to-teal-500'
  }
];

const categories = [
  { name: 'Digital Strategy', count: 'Coming Soon' },
  { name: 'Web Development', count: 'Coming Soon' },
  { name: 'AI & Automation', count: 'Coming Soon' },
  { name: 'Entrepreneurship', count: 'Coming Soon' },
  { name: 'Event Technology', count: 'Coming Soon' },
  { name: 'SEO & Marketing', count: 'Coming Soon' },
];

const blogSchema = {
  "@context": "https://schema.org",
  "@type": "Blog",
  "name": "Fearless IT Insights",
  "description": "Practical insights on technology, digital strategy, and business growth from the Fearless IT team.",
  "publisher": {
    "@type": "Organization",
    "name": "Fearless IT",
    "url": "https://fearlessit.io"
  }
};

const BlogPage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-white" data-testid="blog-page">
      <SEO 
        title="Insights & Blog - Technology Tips and Business Growth"
        description="Practical insights on technology, digital strategy, and business growth from the Fearless IT team. Tips, guides, and thought leadership for modern businesses."
        keywords="technology blog, business growth tips, digital strategy insights, web development blog, AI automation tips, Las Vegas tech blog, small business technology"
        url="/resources/blog"
        structuredData={blogSchema}
      />
      
      <MarketingHeader />

      {/* Hero Section */}
      <section className="pt-16 pb-12 sm:pt-20 sm:pb-16 bg-gradient-to-b from-orange-50 to-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-orange-100 rounded-full mb-6">
            <BookOpen className="h-4 w-4 text-orange-600" />
            <span className="text-sm font-medium text-orange-700">Blog</span>
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-carbon-black mb-4" data-testid="blog-title">
            Insights & Blog
          </h1>
          <p className="text-xl sm:text-2xl font-semibold mb-6" style={{ background: 'linear-gradient(to right, #EA580C, #F97316)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
            Practical Knowledge for Growing Businesses
          </p>
          <p className="text-lg sm:text-xl text-slate-gray max-w-2xl mx-auto leading-relaxed">
            Tips, guides, and thought leadership on technology, digital strategy, and building businesses that thrive.
          </p>
        </div>
      </section>

      {/* Coming Soon Notice */}
      <section className="py-8 bg-gradient-to-r from-orange-500 to-amber-500">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <p className="text-white font-semibold">
            Our blog is launching soon! Subscribe to be notified when new articles are published.
          </p>
        </div>
      </section>

      {/* Featured Posts Preview */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-carbon-black mb-4">
              Coming Soon
            </h2>
            <p className="text-lg text-slate-gray max-w-2xl mx-auto">
              Here's a preview of the topics we'll be covering.
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {featuredPosts.map((post, idx) => {
              const IconComponent = post.icon;
              return (
                <div key={idx} className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden group">
                  <div className={`h-32 bg-gradient-to-r ${post.gradient} flex items-center justify-center`}>
                    <IconComponent className="h-12 w-12 text-white opacity-80" />
                  </div>
                  <div className="p-6">
                    <div className="flex items-center gap-3 mb-3">
                      <span className="text-xs font-semibold text-orange-600 uppercase tracking-wider">
                        {post.category}
                      </span>
                    </div>
                    <h3 className="text-lg font-bold text-carbon-black mb-3 group-hover:text-orange-600 transition-colors">
                      {post.title}
                    </h3>
                    <p className="text-sm text-slate-gray mb-4 leading-relaxed">
                      {post.excerpt}
                    </p>
                    <div className="flex items-center gap-4 text-xs text-slate-gray">
                      <span className="flex items-center gap-1">
                        <Calendar className="h-3 w-3" />
                        {post.date}
                      </span>
                      <span className="flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        {post.readTime}
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Categories */}
      <section className="py-16 sm:py-20 bg-gradient-to-br from-slate-50 to-gray-100">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-12">
            <h2 className="text-2xl sm:text-3xl font-bold text-carbon-black mb-4">
              Topics We'll Cover
            </h2>
          </div>
          
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {categories.map((category, idx) => (
              <div key={idx} className="flex items-center justify-between p-4 bg-white rounded-xl border border-gray-100">
                <span className="font-medium text-carbon-black">{category.name}</span>
                <span className="text-xs text-slate-gray bg-gray-100 px-2 py-1 rounded-full">
                  {category.count}
                </span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Newsletter CTA */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <div className="bg-gradient-to-br from-orange-50 to-amber-50 rounded-2xl p-8 sm:p-12 border border-orange-100 text-center">
            <Zap className="h-12 w-12 text-orange-500 mx-auto mb-6" />
            <h2 className="text-2xl sm:text-3xl font-bold text-carbon-black mb-4">
              Stay in the Loop
            </h2>
            <p className="text-lg text-slate-gray mb-8 max-w-xl mx-auto">
              Get notified when we publish new articles, guides, and resources. No spam, just valuable insights.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact">
                <Button className="bg-gradient-to-r from-orange-500 to-amber-500 text-white px-8 py-6 text-lg font-semibold rounded-xl hover:from-orange-600 hover:to-amber-600 transition-all w-full sm:w-auto">
                  Subscribe for Updates
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Alternative CTA */}
      <section className="py-16 sm:py-20 bg-gradient-to-br from-orange-50 to-amber-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <h2 className="text-2xl sm:text-3xl font-bold text-carbon-black mb-4">
            Can't Wait for the Blog?
          </h2>
          <p className="text-lg text-slate-gray mb-8 max-w-2xl mx-auto">
            Get personalized insights through a free Growth Audit or reach out directly to discuss your challenges.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/growth-audit">
              <Button className="gradient-fearless text-white px-8 py-6 text-lg font-semibold rounded-xl hover:opacity-90 transition-all w-full sm:w-auto">
                Free Growth Audit
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
            <Link to="/contact">
              <Button variant="outline" className="px-8 py-6 text-lg font-semibold rounded-xl border-2 border-orange-400 text-orange-600 hover:bg-orange-50 transition-all w-full sm:w-auto">
                Contact Us
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <MarketingFooter />
    </div>
  );
};

export default BlogPage;
